import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from '@/components/ui';
import { ProjectDocumentCreateData } from '../../../types/ProjectDocument';

interface OtherDocumentModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (data: ProjectDocumentCreateData, file?: File) => Promise<void>;
}

const OtherDocumentModal: React.FC<OtherDocumentModalProps> = ({ visible, onClose, onSave }) => {
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (!selectedFile) throw new Error('A PDF file is required');
      await onSave({ attachmentNotes: notes || undefined }, selectedFile);
      setNotes('');
      setSelectedFile(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNotes('');
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  return (
    <Modal show={visible} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Other Document</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={12}>
              <Form.Group className="tw-mb-3">
                <Form.Label>PDF File *</Form.Label>
                <Form.Control type="file" accept=".pdf" onChange={handleFileChange} />
                <Form.Text className="tw-text-content-muted">Only PDF files are allowed. Maximum size: 10MB</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Optional notes about this document"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {selectedFile && (
            <div className="tw-text-success">
              <small>
                <i className="bi bi-check-circle tw-mr-1" />
                New file selected: {selectedFile.name}
              </small>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Uploading...' : 'Add Document'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OtherDocumentModal;

